import { useWorkspacesPageListQuery } from '../../../generated';
import type { Workspace } from '../types';
import { mapToWorkspaces } from '../utils';

export interface UseWorkspacesResult {
  workspaces: Workspace[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// The admin plane's whole list in one read. It pages, so this asks for the
// cap; a workspace count beyond that wants a pager on the page rather than a
// larger number here.
const WORKSPACE_PAGE_SIZE = 200;

export const useWorkspaces = (): UseWorkspacesResult => {
  const { data, loading, error, refetch } = useWorkspacesPageListQuery({
    variables: { input: { limit: WORKSPACE_PAGE_SIZE } },
    fetchPolicy: 'cache-and-network',
  });

  const result = data?.workspaceList;

  // Listing workspaces is admin-only, so a non-admin reaches the page and gets
  // a refusal rather than an empty list — worth showing as an error.
  const authorizationError =
    result?.__typename === 'AuthorizationFailure'
      ? new Error(result.message ?? 'Not authorized to view workspaces.')
      : null;

  return {
    workspaces:
      result?.__typename === 'WorkspaceListResult'
        ? mapToWorkspaces(result.workspaces)
        : [],
    isLoading: loading,
    error: error ?? authorizationError,
    refetch: () => {
      refetch();
    },
  };
};
